import { type JSX } from 'preact';
import { useState } from 'preact/hooks';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Alert,
  QRCodeDisplay,
} from '../../components';
import { splitSecret, formatShareForDisplay, type ShamirShare } from '../../crypto';
import { navigateTo } from '../../App';

export function SplitSecretPage(): JSX.Element {
  const [secret, setSecret] = useState('');
  const [totalShares, setTotalShares] = useState('5');
  const [threshold, setThreshold] = useState('3');
  const [shares, setShares] = useState<ShamirShare[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [copiedIndex, setCopiedIndex] = useState<number | null>(null);

  const handleSplit = async (e: JSX.TargetedEvent<HTMLFormElement, Event>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setShares([]);

    try {
      // Validation
      if (!secret.trim()) {
        throw new Error('Please enter a secret to split');
      }

      const n = parseInt(totalShares, 10);
      const k = parseInt(threshold, 10);

      if (isNaN(n) || n < 2 || n > 255) {
        throw new Error('Total shares must be between 2 and 255');
      }

      if (isNaN(k) || k < 2 || k > n) {
        throw new Error('Threshold must be between 2 and total shares');
      }

      // Split the secret
      const generatedShares = await splitSecret(secret, n, k);
      setShares(generatedShares);

      // Clear the secret input for security
      setSecret('');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An error occurred';
      setError(message);
    }
  };

  const handleCopy = (share: string, index: number): void => {
    navigator.clipboard.writeText(share);
    setCopiedIndex(index);
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  const handleReset = (): void => {
    setShares([]);
    setSecret('');
    setTotalShares('5');
    setThreshold('3');
    setError(null);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Split Secret with Shamir Secret Sharing</CardTitle>
          <CardDescription>
            Divide your secret into multiple shares. Any {threshold} of {totalShares || 'N'} shares
            can reconstruct the original secret.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Alert type="info" title="How It Works" className="mb-6">
            <ul className="list-disc list-inside space-y-1">
              <li>Your secret is split into multiple shares using cryptographic threshold sharing</li>
              <li>Shares use base32 encoding - easy to type and write down</li>
              <li>All processing happens in your browser - the secret never leaves your device</li>
              <li>Distribute shares to trusted parties (e.g., 3 of 5 can recover the secret)</li>
            </ul>
            <p className="mt-3 font-semibold">
              <a
                href="/shamir/specification"
                onClick={(e) => {
                  e.preventDefault();
                  navigateTo('/shamir/specification');
                }}
                className="text-blue-600 hover:underline cursor-pointer"
              >
                📄 View Technical Specification
              </a>{' '}
              - Save this page to reconstruct secrets even if this service goes offline
            </p>
          </Alert>

          {error && (
            <Alert type="error" title="Error" className="mb-6">
              {error}
            </Alert>
          )}

          {shares.length === 0 ? (
            <form onSubmit={handleSplit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secret to Split (required)
                </label>
                <textarea
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="Enter the secret you want to split"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 bg-white dark:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  rows={6}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Input
                    label="Total Shares (n)"
                    type="number"
                    value={totalShares}
                    onChange={(e) => setTotalShares(e.target.value)}
                    placeholder="5"
                    min="2"
                    max="255"
                  />
                  <p className="text-xs text-gray-500 mt-1">How many shares to create</p>
                </div>

                <div>
                  <Input
                    label="Threshold (k)"
                    type="number"
                    value={threshold}
                    onChange={(e) => setThreshold(e.target.value)}
                    placeholder="3"
                    min="2"
                    max={totalShares}
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum shares needed to reconstruct</p>
                </div>
              </div>

              <div className="flex gap-2 justify-end pt-4">
                <Button type="submit" size="lg">
                  Split Secret
                </Button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <Alert type="success" title="Secret Split Successfully!" className="mb-6">
                <p>
                  Your secret has been split into {shares.length} shares. Any {threshold} shares
                  can reconstruct the original secret.
                </p>
                <p className="mt-2 font-semibold">
                  Distribute these shares to trusted parties. Keep them separate and secure.
                </p>
                <p className="mt-3 px-4 py-3 bg-green-100 rounded border border-green-300">
                  <span className="font-semibold">📄 Print this page</span> to get one page per share with a scannable QR code for easy distribution.
                </p>
              </Alert>

              <div className="space-y-3">
                {shares.map((shareObj, index) => (
                  <div key={index} className="shamir-share border border-gray-300 rounded-md p-4 bg-white">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-gray-900">Share {shareObj.index}</h3>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => handleCopy(shareObj.share, index)}
                      >
                        {copiedIndex === index ? 'Copied!' : 'Copy'}
                      </Button>
                    </div>
                    <div className="font-mono text-sm bg-gray-50 p-3 rounded border border-gray-200 break-all mb-3">
                      {formatShareForDisplay(shareObj.share)}
                    </div>

                    {/* QR Code for easy scanning */}
                    <div className="flex justify-center">
                      <QRCodeDisplay
                        value={shareObj.share}
                        size={150}
                        title={`Scan share ${shareObj.index}`}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <Alert type="warning" title="Security Reminder" className="mt-6">
                <ul className="list-disc list-inside space-y-1">
                  <li>Write down or securely store each share separately</li>
                  <li>Never store all shares in the same location</li>
                  <li>The original secret has been cleared from this page</li>
                  <li>Anyone with {threshold} or more shares can reconstruct your secret</li>
                </ul>
              </Alert>

              <div className="flex gap-2 justify-end pt-4">
                <Button onClick={handleReset} size="lg">
                  Split Another Secret
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
